/**
 * O domo do Will.
 *
 * Reconstrução em React/SVG do rosto que existia no app legado
 * (docs/legacy-app-reference.html). O legado desenhava o rosto com dezenas de
 * <div> posicionadas em `clamp()`; aqui tudo vive num único SVG de viewBox
 * fixo. PORQUÊ: o rosto passa a escalar por semelhança em vez de por uma
 * tabela de tamanhos responsivos — as proporções entre olho, sobrancelha e
 * boca deixam de depender da largura da janela, que era a maior fonte de
 * deriva visual do original.
 *
 * Cor, rótulo e textos vêm de DOME[state] (@will/contracts). Os traços de
 * desenho de cada expressão vêm de FACE[state] (./faces).
 */

import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import { DOME, type DomeState } from '@will/contracts';
import { FACE, type BrowShape, type FaceTraits } from './faces';
import { ensureDomeStyles } from './domeStyles';

// injeta na carga do módulo, não em efeito: o primeiro quadro já sai estilizado
ensureDomeStyles();

export interface DomeProps {
  /** expressão afetiva atual */
  state: DomeState;
  /** nível de áudio, 0..1 — abre a boca e acende a aura */
  level: number;
  /** há fala em curso: mostra a boca mesmo fora de `speaking` */
  talking: boolean;
  className?: string;
}

/* ── Geometria, em unidades do viewBox (320 × 320) ───────────────────────── */

const VB = 320;
const CX = VB / 2;

const EYE_W = 58;
const EYE_H = 74;
const EYE_H_SLEEPY = 20; // pálpebra quase fechada do descanso
const EYE_RX = EYE_W / 2;
const EYE_CY = 148;
const EYE_DX = 43; // meia-distância entre os centros dos olhos

const BROW_W = 52;
const BROW_H = 7;
const BROW_GAP = 20; // folga entre a sobrancelha e o topo do olho

const MOUTH_BARS = 7;
const MOUTH_W = 7;
const MOUTH_STEP = 12;
const MOUTH_H = 26;
const MOUTH_CY = 216;

const eyeCx = (side: Side): number => (side === 'l' ? CX - EYE_DX : CX + EYE_DX);

type Side = 'l' | 'r';

/** Variante translúcida de uma cor da paleta (todas são hex de 6 dígitos). */
const alpha = (hex: string, aa: string): string => hex + aa;

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : Number.isFinite(n) ? n : 0);

/* ── Hooks ───────────────────────────────────────────────────────────────── */

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent): void => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/**
 * Escreve o nível suavizado numa custom property em vez de em estado React.
 *
 * PORQUÊ: o nível chega a cada quadro de áudio; passar por setState aqui
 * re-renderizaria o rosto inteiro ~50 vezes por segundo para mexer só na
 * altura da boca. A propriedade `--dome-level` é lida pelo CSS e o React nunca
 * precisa saber.
 */
function useLevelChannel(
  ref: RefObject<HTMLDivElement>,
  level: number,
  reduced: boolean,
): void {
  const target = useRef(0);
  useEffect(() => {
    target.current = clamp01(level);
    if (reduced) ref.current?.style.setProperty('--dome-level', target.current.toFixed(3));
  }, [level, reduced, ref]);

  useEffect(() => {
    if (reduced) return;
    let raf = 0;
    let current = 0;
    const tick = (): void => {
      // interpolação leve: a leitura do microfone é ruidosa demais para ir crua
      current += (target.current - current) * 0.22;
      ref.current?.style.setProperty('--dome-level', current.toFixed(3));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reduced, ref]);
}

/**
 * Respiração: amplitude e ritmo por expressão.
 *
 * Não é enfeite — é o que separa "em repouso" de "congelado". Quem descansa
 * respira fundo e devagar; quem executa respira curto. A amplitude fica na
 * casa de 1% de propósito: precisa ser percebida sem ser notada.
 */
const RESPIRO: Partial<Record<DomeState, { amp: number; dur: number }>> = {
  resting: { amp: 1.03, dur: 7.8 },
  sad: { amp: 1.022, dur: 6.6 },
  listening: { amp: 1.014, dur: 5.2 },
  awaiting: { amp: 1.013, dur: 4.9 },
  thinking: { amp: 1.011, dur: 4.2 },
  focus: { amp: 1.009, dur: 4.6 },
  worried: { amp: 1.016, dur: 3.4 },
  speaking: { amp: 1.008, dur: 3.4 },
  busy: { amp: 1.006, dur: 2.7 },
  waking: { amp: 1.018, dur: 3.0 },
};
const RESPIRO_PADRAO = { amp: 1.012, dur: 5.2 };

/**
 * Frequência de piscada, relativa ao intervalo base.
 *
 * Maior que 1 = pisca mais. Quem fala pisca mais que quem escuta, e quem está
 * inquieto pisca bem mais — é assim que o olho denuncia o estado antes de a
 * legenda dizer. `resting` não entra: lá a pálpebra já está fechada pela
 * animação de sono, e piscar por cima dela produziria um tique.
 */
const PISCA: Partial<Record<DomeState, number>> = {
  worried: 1.7,
  unclear: 1.5,
  speaking: 1.35,
  opportunity: 1.25,
  awaiting: 1.15,
  thinking: 0.75,
  focus: 0.6,
  failed: 0.55,
};

const BLINK_BASE_MS = 4_200;
const BLINK_JITTER = 0.62;
const BLINK_FECHADO_MS = 118;
const BLINK_DUPLA = 0.17;

/**
 * Piscada com intervalo sorteado e dupla ocasional.
 *
 * O laço de CSS que existia antes tinha período fixo — e período fixo lê como
 * máquina, por mais bonito que seja o desenho. Aqui o intervalo varia em torno
 * do base e uma piscada em cada seis vem em par, que é o padrão do olho de
 * verdade. Escreve em atributo, não em estado do React: são dezenas de
 * disparos por minuto e nenhum deles muda o que a interface *é*.
 */
function useBlink(ref: RefObject<HTMLDivElement>, taxa: number, ativo: boolean): void {
  useEffect(() => {
    const no = ref.current;
    if (!no || !ativo) return;

    let vivo = true;
    const pendentes: number[] = [];
    const depois = (ms: number, fn: () => void): void => {
      pendentes.push(window.setTimeout(fn, ms));
    };

    const fechar = (dupla: boolean): void => {
      if (!vivo) return;
      no.setAttribute('data-piscar', '1');
      depois(BLINK_FECHADO_MS, () => {
        if (!vivo) return;
        no.removeAttribute('data-piscar');
        if (dupla) depois(150, () => fechar(false));
      });
    };

    const agendar = (): void => {
      if (!vivo) return;
      const base = BLINK_BASE_MS / taxa;
      const espera = base * (1 - BLINK_JITTER + Math.random() * BLINK_JITTER * 2);
      depois(espera, () => {
        fechar(Math.random() < BLINK_DUPLA);
        agendar();
      });
    };
    agendar();

    return () => {
      vivo = false;
      for (const t of pendentes) window.clearTimeout(t);
      no.removeAttribute('data-piscar');
    };
  }, [ref, taxa, ativo]);
}

/* ── Componente ──────────────────────────────────────────────────────────── */

export function Dome({ state, level, talking, className }: DomeProps): JSX.Element {
  const expr = DOME[state];
  const face = FACE[state];
  const col = expr.color;

  const rootRef = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();
  useLevelChannel(rootRef, level, reduced);

  const respiro = RESPIRO[state] ?? RESPIRO_PADRAO;
  // `sleepy` já tem a pálpebra baixa pela animação de sono; piscar por cima
  // seria um tique. E quem pediu menos movimento não pisca.
  useBlink(rootRef, PISCA[state] ?? 1, !reduced && !face.sleepy);

  // useId traz ':' — inválido dentro de url(#…) em vários motores
  const uid = `dome-${useId().replace(/:/g, '')}`;

  const showMouth = Boolean(face.mouth) || talking;

  return (
    <div
      ref={rootRef}
      className={className ? `dome-root ${className}` : 'dome-root'}
      style={
        {
          '--dome-level': '0',
          '--dome-respiro-amp': String(respiro.amp),
          '--dome-respiro-dur': `${respiro.dur}s`,
        } as CSSProperties
      }
      data-state={state}
    >
      <div
        className="dome-aura"
        style={{ background: `radial-gradient(circle, ${alpha(col, '4d')} 0%, transparent 62%)` }}
      />
      <div
        className="dome-ring"
        style={{
          background:
            `conic-gradient(from 200deg, ${alpha(col, '1f')}, ${alpha(col, 'f2')} 22%, ` +
            `${col} 34%, ${alpha(col, '24')} 52%, ${alpha(col, '8c')} 74%, ${alpha(col, '1a')} 100%)`,
          boxShadow: `0 0 42px ${alpha(col, '52')}, 0 22px 70px rgba(0,0,0,.62)`,
        }}
      >
        <div className="dome-shell" />
      </div>
      <div className="dome-led" />

      <svg
        className="dome-face"
        viewBox={`0 0 ${VB} ${VB}`}
        role="img"
        aria-label={`${expr.label}. ${expr.sub}`}
      >
        <defs>
          <radialGradient id={`${uid}-eye`} cx="50%" cy="28%" r="60%">
            <stop offset="0%" stopColor="#f2fdff" />
            <stop offset="40%" stopColor={col} />
            <stop offset="74%" stopColor={alpha(col, 'd9')} />
            <stop offset="100%" stopColor="rgba(6,16,26,.9)" />
          </radialGradient>
          <linearGradient id={`${uid}-brow`} x1="0" x2="1">
            <stop offset="0%" stopColor={alpha(col, '73')} />
            <stop offset="50%" stopColor={col} />
            <stop offset="100%" stopColor={alpha(col, '73')} />
          </linearGradient>
          <linearGradient id={`${uid}-line`} x1="0" x2="1">
            <stop offset="0%" stopColor={col} stopOpacity="0" />
            <stop offset="50%" stopColor={col} stopOpacity="1" />
            <stop offset="100%" stopColor={col} stopOpacity="0" />
          </linearGradient>
          <clipPath id={`${uid}-clip-l`}>
            <EyeShape side="l" sleepy={Boolean(face.sleepy)} />
          </clipPath>
          <clipPath id={`${uid}-clip-r`}>
            <EyeShape side="r" sleepy={Boolean(face.sleepy)} />
          </clipPath>
        </defs>

        {face.halo && <Halo color={col} />}

        <g className={face.tilt ? 'dome-fx dome-a-face-tilt' : undefined}>
          {face.nodes && <Nodes color={col} />}

          <g className={`dome-fx ${gazeClass(face)}`}>
            <Eye side="l" uid={uid} color={col} face={face} />
            <Eye side="r" uid={uid} color={col} face={face} />
          </g>

          {face.sweep && (
            <rect
              className="dome-fx dome-a-sweep"
              x={CX - 55}
              y={211}
              width={110}
              height={2}
              rx={1}
              fill={`url(#${uid}-line)`}
              style={{ filter: `drop-shadow(0 0 6px ${col})` }}
            />
          )}

          {/* boca em repouso e boca falando ocupam o mesmo lugar; quando há
              fala, a estática sai de cena em vez de se sobrepor */}
          {face.softSmile && !showMouth && (
            // o legado calculava `softSmileStroke` e nunca chegou a desenhá-lo —
            // o template só tinha o ramo `face === 'smile'`, morto. Aqui o
            // sorriso do estado "concluído" finalmente aparece.
            <path
              d={`M${CX - 48} 208 Q${CX} 236 ${CX + 48} 208`}
              fill="none"
              stroke={col}
              strokeWidth={5}
              strokeLinecap="round"
              opacity={0.82}
              style={{ filter: `drop-shadow(0 0 6px ${alpha(col, '99')})` }}
            />
          )}

          {face.sadMouth && !showMouth && (
            <path
              d={`M${CX - 18} 216 Q${CX} 202 ${CX + 18} 216`}
              fill="none"
              stroke={col}
              strokeWidth={3.5}
              strokeLinecap="round"
              opacity={0.68}
              style={{ filter: `drop-shadow(0 0 5px ${alpha(col, '80')})` }}
            />
          )}

          {face.droop && !showMouth && (
            // mesma história do sorriso: `droopStyle` existia sem consumidor
            <path
              d={`M${CX - 36} 220 Q${CX} 198 ${CX + 36} 220`}
              fill="none"
              stroke={col}
              strokeWidth={4}
              strokeLinecap="round"
              style={{ filter: `drop-shadow(0 0 7px ${col})` }}
            />
          )}

          {face.boot && <BootBars color={col} />}
          {face.zzz && <Zzz color={col} />}
          {showMouth && <Mouth color={col} />}
          {face.badge && <Badge char={face.badge} color={col} />}
        </g>
      </svg>
    </div>
  );
}

export default Dome;

/* ── Peças do rosto ──────────────────────────────────────────────────────── */

/** O olhar: cada expressão tem o seu, e o sono não tem nenhum. */
function gazeClass(face: FaceTraits): string {
  if (face.sleepy) return '';
  if (face.tears) return 'dome-a-gaze-sad';
  if (face.tilt) return 'dome-a-gaze-tilt';
  if (face.shrug) return 'dome-a-gaze-shrug';
  if (face.ponder) return 'dome-a-gaze-ponder';
  return 'dome-a-gaze';
}

const eyeGeom = (side: Side, sleepy: boolean) => {
  const h = sleepy ? EYE_H_SLEEPY : EYE_H;
  return {
    x: eyeCx(side) - EYE_W / 2,
    y: EYE_CY - h / 2,
    width: EYE_W,
    height: h,
    rx: Math.min(EYE_RX, h / 2),
  };
};

function EyeShape({ side, sleepy }: { side: Side; sleepy: boolean }): JSX.Element {
  return <rect {...eyeGeom(side, sleepy)} />;
}

function Eye({
  side,
  uid,
  color,
  face,
}: {
  side: Side;
  uid: string;
  color: string;
  face: FaceTraits;
}): JSX.Element {
  const sleepy = Boolean(face.sleepy);
  const g = eyeGeom(side, sleepy);
  const cx = eyeCx(side);
  const top = EYE_CY - EYE_H / 2;
  const bottom = EYE_CY + EYE_H / 2;

  const cellAnim = face.streak ? (side === 'l' ? 'dome-a-dash' : 'dome-a-dash-r') : '';

  return (
    <g className={cellAnim ? `dome-fx ${cellAnim}` : undefined}>
      {/* as linhas de velocidade passam por trás do olho: só as pontas aparecem */}
      {face.streak &&
        [0, 1, 2].map((i) => (
          <rect
            key={i}
            className={`dome-fx dome-a-streak-${i}`}
            x={cx - 47}
            y={EYE_CY - 24 + i * 24}
            width={94}
            height={2}
            rx={1}
            fill={`url(#${uid}-line)`}
            opacity={0.65}
            style={{ filter: 'blur(1.2px)' }}
          />
        ))}

      <Brow side={side} uid={uid} color={color} brow={face.brow} zzz={Boolean(face.zzz)} />

      <g className={`dome-fx ${sleepy ? 'dome-a-sleep' : 'dome-a-blink'}`}>
        <rect
          {...g}
          fill={`url(#${uid}-eye)`}
          style={{ filter: `drop-shadow(0 0 14px ${alpha(color, 'b3')}) drop-shadow(0 0 32px ${alpha(color, '59')})` }}
        />
        <g clipPath={`url(#${uid}-clip-${side})`}>
          {face.scan && <ScanLines top={top} bottom={bottom} cx={cx} />}
          {face.arcDown && (
            <path
              d={`M${cx - 18} ${bottom - 35} Q${cx} ${bottom + 5} ${cx + 18} ${bottom - 35}`}
              fill="none"
              stroke="rgba(5,16,26,.82)"
              strokeWidth={3}
            />
          )}
          {face.arcUp && (
            <path
              d={`M${cx - 18} ${top + 41} Q${cx} ${top + 9} ${cx + 18} ${top + 41}`}
              fill="none"
              stroke="rgba(5,16,26,.82)"
              strokeWidth={3}
            />
          )}
        </g>
      </g>

      {/* a lágrima é só do olho esquerdo, como no original — lá ela ficava
          dentro do olho, com overflow:hidden, e nunca aparecia; aqui escorre
          por fora, que é o que a expressão pedia */}
      {face.tears && side === 'l' && (
        // o translate fica num grupo à parte porque a animação escreve em
        // `transform` — e transform vindo do CSS substitui o do atributo
        <g transform={`translate(${g.x - 3} ${bottom - 6})`}>
          <g className="dome-fx dome-a-tear" style={{ filter: `drop-shadow(0 0 6px ${color})` }}>
            <path
              d="M0 -7 C3 -3 4.5 -1 4.5 1.5 A4.5 4.5 0 0 1 -4.5 1.5 C-4.5 -1 -3 -3 0 -7 Z"
              fill={color}
              opacity={0.92}
            />
          </g>
        </g>
      )}
    </g>
  );
}

function Brow({
  side,
  uid,
  color,
  brow,
  zzz,
}: {
  side: Side;
  uid: string;
  color: string;
  brow: BrowShape;
  zzz: boolean;
}): JSX.Element {
  const deg = side === 'l' ? brow.l : brow.r;
  // `lift` ergue só a esquerda: é a assimetria da dúvida, não um offset comum
  const dy = brow.lift && side === 'l' ? -7 : brow.y;
  const cx = eyeCx(side);
  const cy = EYE_CY - EYE_H / 2 - BROW_GAP + dy;

  const anim = zzz
    ? side === 'l'
      ? 'dome-a-brow-rem'
      : 'dome-a-brow-rem-r'
    : brow.fret
      ? side === 'l'
        ? 'dome-a-brow-fret'
        : 'dome-a-brow-fret-r'
      : side === 'l'
        ? 'dome-a-brow'
        : 'dome-a-brow-r';

  return (
    <g className={`dome-fx ${anim}`}>
      <rect
        x={cx - BROW_W / 2}
        y={cy - BROW_H / 2}
        width={BROW_W}
        height={BROW_H}
        rx={BROW_H / 2}
        fill={`url(#${uid}-brow)`}
        transform={`rotate(${deg} ${cx} ${cy})`}
        style={{ filter: `drop-shadow(0 0 7px ${color}) drop-shadow(0 0 14px ${alpha(color, '80')})` }}
      />
    </g>
  );
}

function ScanLines({ top, bottom, cx }: { top: number; bottom: number; cx: number }): JSX.Element {
  const lines: number[] = [];
  for (let y = top - 9; y < bottom + 9; y += 9) lines.push(y);
  return (
    <g className="dome-a-scan">
      {lines.map((y) => (
        <rect key={y} x={cx - EYE_W / 2} y={y} width={EYE_W} height={1.5} fill="rgba(4,10,20,.34)" />
      ))}
    </g>
  );
}

function Halo({ color }: { color: string }): JSX.Element {
  const r = 124;
  const c = 2 * Math.PI * r;
  return (
    <g>
      <g className="dome-fx dome-a-halo-a" style={{ filter: `drop-shadow(0 0 6px ${color})` }}>
        <circle
          cx={CX}
          cy={EYE_CY}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeDasharray={`${c * 0.26} ${c}`}
        />
        <circle
          cx={CX}
          cy={EYE_CY}
          r={r}
          fill="none"
          stroke={alpha(color, '4d')}
          strokeWidth={2}
          strokeDasharray={`${c * 0.26} ${c}`}
          strokeDashoffset={-c * 0.26}
        />
      </g>
      <circle
        className="dome-fx dome-a-halo-b"
        cx={CX}
        cy={EYE_CY}
        r={r - 12}
        fill="none"
        stroke={alpha(color, '7a')}
        strokeWidth={1.5}
        strokeDasharray="6 8"
      />
      <circle
        className="dome-fx dome-a-halo-pulse"
        cx={CX}
        cy={EYE_CY}
        r={r - 6}
        fill="none"
        stroke={color}
        strokeWidth={1}
      />
    </g>
  );
}

/** Grafo de nós: o raciocínio ligando pontos acima dos olhos. */
function Nodes({ color }: { color: string }): JSX.Element {
  const pts: Array<[number, number]> = [
    [14, 34],
    [62, 12],
    [110, 30],
    [158, 10],
    [206, 32],
  ];
  const path = pts.map(([x, y]) => `${x},${y}`).join(' ');
  return (
    <g transform="translate(52 26)" style={{ filter: `drop-shadow(0 0 6px ${color})` }}>
      <polyline points={path} fill="none" stroke={alpha(color, '59')} strokeWidth={1.5} strokeDasharray="4 5" />
      <polyline
        className="dome-a-trace"
        points={path}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeDasharray="26 300"
      />
      {pts.map(([x, y], i) => (
        <circle key={i} className={`dome-fx dome-a-node-${i}`} cx={x} cy={y} r={i === 2 ? 6 : 4.5} fill={color} />
      ))}
    </g>
  );
}

function BootBars({ color }: { color: string }): JSX.Element {
  return (
    <g style={{ filter: `drop-shadow(0 0 6px ${color})` }}>
      {[0, 1, 2, 3].map((i) => (
        <rect
          key={i}
          className={`dome-fx-left dome-a-boot-${i}`}
          x={56 + i * 54}
          y={208}
          width={46}
          height={4}
          rx={2}
          fill={color}
        />
      ))}
    </g>
  );
}

function Zzz({ color }: { color: string }): JSX.Element {
  const items: Array<{ ch: string; x: number; size: number }> = [
    { ch: 'z', x: 186, size: 16 },
    { ch: 'z', x: 206, size: 21 },
    { ch: 'Z', x: 230, size: 28 },
  ];
  return (
    <g style={{ filter: `drop-shadow(0 0 8px ${color})` }}>
      {items.map((z, i) => (
        <text
          key={i}
          className={`dome-fx dome-a-zzz-${i}`}
          x={z.x}
          y={214}
          fill={color}
          fontSize={z.size}
          fontWeight={700}
          textAnchor="middle"
        >
          {z.ch}
        </text>
      ))}
    </g>
  );
}

/** Barras de fala. A altura vem do CSS (--dome-level); o ritmo, dos keyframes. */
function Mouth({ color }: { color: string }): JSX.Element {
  const x0 = CX - ((MOUTH_BARS - 1) * MOUTH_STEP + MOUTH_W) / 2;
  return (
    <g className="dome-mouth" style={{ filter: `drop-shadow(0 0 6px ${color})` }}>
      {Array.from({ length: MOUTH_BARS }, (_, i) => (
        <rect
          key={i}
          className={`dome-fx dome-a-mouth-${i}`}
          x={x0 + i * MOUTH_STEP}
          y={MOUTH_CY - MOUTH_H / 2}
          width={MOUTH_W}
          height={MOUTH_H}
          rx={MOUTH_W / 2}
          fill={color}
        />
      ))}
    </g>
  );
}

function Badge({ char, color }: { char: string; color: string }): JSX.Element {
  return (
    <g style={{ filter: `drop-shadow(0 0 7px ${alpha(color, '80')})` }}>
      <circle cx={250} cy={212} r={13} fill="none" stroke={color} strokeWidth={2} />
      <text
        x={250}
        y={212}
        dy=".35em"
        fill={color}
        fontSize={16}
        fontWeight={600}
        textAnchor="middle"
      >
        {char}
      </text>
    </g>
  );
}
